import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown, Loader2 } from "lucide-react";
import { api, errMsg, fmtNum } from "@/lib/api";
import { cn } from "@/lib/utils";

export function useDebounced(value, ms = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export default function DataTable({ endpoint, params = {}, columns, onRowClick, testid = "data-table", pageSize = 50, reloadKey, rowClass, onSortChange }) {
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState({ key: null, dir: "asc" });
  useEffect(() => { onSortChange?.(sort); }, [sort]); // eslint-disable-line react-hooks/exhaustive-deps
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [ms, setMs] = useState(null);
  const pkey = JSON.stringify(params);
  const reqId = useRef(0);

  useEffect(() => setPage(1), [pkey]);

  const load = useCallback(async () => {
    const id = ++reqId.current;
    setLoading(true);
    const t0 = performance.now();
    try {
      const clean = Object.fromEntries(Object.entries(JSON.parse(pkey)).filter(([, v]) => v !== "" && v != null && v !== "all"));
      const r = await api.get(endpoint, { params: { ...clean, page, page_size: pageSize, sort: sort.key || undefined, dir: sort.dir } });
      if (id !== reqId.current) return;
      setData(r.data);
      setError(null);
      setMs(Math.round(performance.now() - t0));
    } catch (e) {
      if (id === reqId.current) setError(errMsg(e));
    } finally {
      if (id === reqId.current) setLoading(false);
    }
  }, [endpoint, pkey, page, pageSize, sort]);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  const pages = Math.max(1, Math.ceil(data.total / pageSize));
  const toggleSort = (k) => setSort((s) => ({ key: k, dir: s.key === k && s.dir === "asc" ? "desc" : "asc" }));

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid={testid}>
      <div className="scroll-thin relative overflow-x-auto">
        {loading && <div className="absolute inset-x-0 top-0 h-0.5 animate-pulse bg-[#D52B3B]" />}
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 bg-slate-50/95 backdrop-blur">
            <tr>
              {columns.map((c) => (
                <th
                  key={c.key}
                  className={cn("whitespace-nowrap px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500", c.thClass)}
                >
                  {c.sortable ? (
                    <button className="inline-flex items-center gap-1 hover:text-slate-800" onClick={() => toggleSort(c.sortKey || c.key)} data-testid={`${testid}-sort-${c.key}`}>
                      {c.label}
                      {sort.key === (c.sortKey || c.key) ? (
                        sort.dir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                      ) : (
                        <ArrowUpDown className="h-3 w-3 opacity-40" />
                      )}
                    </button>
                  ) : (
                    c.label
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.items.map((row, i) => (
              <tr
                key={row.id || i}
                data-testid={`${testid}-row-${i}`}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={cn("transition-colors duration-150", onRowClick && "cursor-pointer hover:bg-slate-50", rowClass?.(row))}
              >
                {columns.map((c) => (
                  <td key={c.key} className={cn("whitespace-nowrap px-4 py-2.5 text-slate-700", c.className)}>
                    {c.render ? c.render(row) : row[c.key] ?? "—"}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {!loading && !error && data.items.length === 0 && (
          <div className="py-14 text-center text-sm text-slate-500" data-testid={`${testid}-empty`}>No records match the current filters.</div>
        )}
        {error && <div className="py-10 text-center text-sm text-rose-600">{error}</div>}
        {loading && data.items.length === 0 && (
          <div className="flex justify-center py-14">
            <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
          </div>
        )}
      </div>
      <div className="flex items-center justify-between border-t border-slate-100 px-4 py-2.5 text-xs text-slate-500">
        <span data-testid={`${testid}-total`}>
          {fmtNum(data.total)} records{ms != null && <span className="ml-2 text-slate-400">· {ms} ms</span>}
        </span>
        <div className="flex items-center gap-2">
          <span data-testid={`${testid}-page`}>
            Page {page} of {fmtNum(pages)}
          </span>
          <button
            data-testid={`${testid}-prev`}
            className="rounded-md border border-slate-200 p-1 transition-colors hover:bg-slate-50 disabled:opacity-40"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            data-testid={`${testid}-next`}
            className="rounded-md border border-slate-200 p-1 transition-colors hover:bg-slate-50 disabled:opacity-40"
            disabled={page >= pages}
            onClick={() => setPage((p) => p + 1)}
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
